import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

import QuestionTypeIcon from '@/components/question/QuestionTypeIcon';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Menu } from '@/components/ui/daisy';
import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { Question } from '@/models/Question';
import { colorsMap } from '@/utils/questions/colors';
import { Fragment } from 'react';

type QuestionSidebarItemProps = {
  question: Question;
  isActive: boolean;
  isExpanded: boolean;
  onSelect: () => void;
  onDelete: () => void;
};

const QuestionSidebarItem = ({
  question,
  isActive,
  isExpanded,
  onSelect,
  onDelete,
}: QuestionSidebarItemProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'questionTypes',
  });

  const { failedIdentifiers } = useQuestionFormsOrchestrator();
  const hasFailed = failedIdentifiers.has(question.identifier);
  const color = hasFailed ? 'warning' : colorsMap[question.type];
  return (
    <Menu.Item>
      <div
        role="button"
        onClick={onSelect}
        className={twJoin(
          'btn btn-ghost font-normal font-poppins text-base',
          color && `btn-${color}`,
          isActive && 'btn-active',
          !isActive && 'not-hover:text-base-content',
          hasFailed && !isActive && 'not-hover:text-warning!',
        )}
      >
        <ConditionalRenderer
          condition={!hasFailed}
          fallback={<ExclamationTriangleIcon className="size-5 shrink-0" />}
        >
          <QuestionTypeIcon type={question.type} className="size-5 shrink-0" />
        </ConditionalRenderer>
        {isExpanded && (
          <Fragment>
            <span className="flex-1 truncate text-left text-sm">
              {t(question.type)}
            </span>
            <Button
              type="button"
              variant="ghost"
              aria-label="Delete question"
              shape="circle"
              size="xs"
              onClick={e => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <XMarkIcon
                className={twJoin('shrink-0 size-4 text-base-content')}
              />
            </Button>
          </Fragment>
        )}
      </div>
    </Menu.Item>
  );
};

export default QuestionSidebarItem;
